/* eslint-disable prettier/prettier */
import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';

import { Button, Col, Container, Row } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import Decimal from 'decimal.js';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Card, CardHeader, CardBody } from '../styles';
import { IParcelas } from '..';

import api from '../../../services/api';
import getResponseErrors from '../../../utils/getResponseErrors';
import { useToast } from '../../../hooks/toast';
import Loading from '../../../components/Loading';
import Checkbox from '../../../components/Exame/CheckBox';
import { formatPrice, updateTimeToNow } from '../../../utils/funcoes';
import FormaPagamento from '../../../components/Pagamento/FormaPagamento';
import { IDesconto } from '../../../components/Pagamento/Desconto';
import { IPagamento } from '../../../components/Pagamento';
import { useClinica } from '../../../hooks/clinica';

interface FormDados {
  CprValor: number;
  CprDataVenc: string;
}

type ComponentProps = {
  parcelas: IParcelas[];
  handleFechar(load: boolean): void;
};

interface IParcelasComSelecao extends IParcelas {
  selecionado?: boolean;
  valorTotal: number;
  valorTotalFormatado: string;
  semAcrescimo?: boolean;
}

const PagamentoEmLote: React.FC<ComponentProps> = ({ parcelas, handleFechar }) => {
  const { addToast } = useToast();
  const { dataHoraServidor } = useClinica();
  const formRefParcela = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [parcelasPagLote, setParcelasPagLote] = useState<IParcelasComSelecao[]>(
    [],
  );

  const [showFuncaoAnonima, setShowFuncaoAnonima] = useState(false);

  const [descontoAplicado, setDescontoAplicado] = useState<IDesconto>({
    UsuCodigo: 0,
    UsuNome: '',
    descricao: '',
    login: '',
    motivo: 0,
    valor: '0',
  });

  const [pagamentosRealizados, setPagamentosRealizados] = useState<
    IPagamento[]
  >([]);

  useEffect(() => {
    const parcelasFormatadas = parcelas.map(p => {
      const valorTotal = Number(
        (p.CprValor + p.CprMulta + p.CprJuros).toFixed(2),
      );

      return {
        ...p,
        valorTotal,
        valorTotalFormatado: formatPrice(valorTotal),
        selecionado: false,
        semAcrescimo: false,
      };
    });
    setParcelasPagLote(parcelasFormatadas);
  }, [parcelas]);


  const total = useMemo(() => {
    const valorTotal = parcelasPagLote
      .filter(parc => parc.selecionado)
      .reduce((acc, parcela) => {
        const valor = new Decimal(parcela.valorTotal);
        return valor.plus(acc).toDecimalPlaces(2).toNumber();
      }, 0);

    return {
      valorTotal,
      valorTotalFormatado: formatPrice(valorTotal),
      count: parcelasPagLote.filter(parc => parc.selecionado).length,
    };
  }, [parcelasPagLote]);


  const valoresAdicionados = useCallback((CprValor: number): boolean => {


    const totalCalculado = new Decimal(pagamentosRealizados[0].PagValor).plus(new Decimal(descontoAplicado.valor)).toDecimalPlaces(2).toNumber();

    if (
      totalCalculado !== CprValor
    ) {
      addToast({
        title: 'Confira os valores',
        description:
          'O total de valores informados são diferentes do total da parcela.',
        type: 'error',
      });
      return false;
    }
    return true;
  }, ([addToast, descontoAplicado.valor, pagamentosRealizados]));


  const handleSubmit = useCallback(
    async (dados: FormDados) => {
      try {

        const CprCodigos = parcelasPagLote.filter(parc => parc.selecionado).map(p => p.CprCodigo);

        if (CprCodigos.length === 0) {
          addToast({
            type: 'error',
            title: 'Nenhum registro selecionado',
          });
          return;
        }

        const totalBateu = valoresAdicionados(
          Number(total.valorTotal),
        );

        if (!totalBateu) return;
        setLoading(true);


        const desconto =
          descontoAplicado.valor !== '0' && descontoAplicado.valor !== ''
            ? {
              CprDesUsuCodigo: descontoAplicado.UsuCodigo,
              CprDesMotCodigo: descontoAplicado.motivo,
              CprDesMotivo: descontoAplicado.descricao.toUpperCase(),
              CprDesconto: new Decimal(descontoAplicado.valor).toDecimalPlaces(2).toNumber(),
            }
            : {};

        const dataPagamento = updateTimeToNow(
          new Date(dataHoraServidor.toString()),
        );

        const pagamentos = pagamentosRealizados.map(pag => {
          return {
            PagData: dataPagamento,
            PagFormaPag: pag.PagFormaPag,
            PagValor: pag.PagValor,
          };
        });


        const dadosEnvio = {
          CprCodigos,
          CprValorPago: new Decimal(pagamentos[0].PagValor).toDecimalPlaces(2).toNumber(),
          valorTotal: total.valorTotal,
          pagamento: pagamentos,
          ...desconto,
        };


        await api.post(
          `/caixa/confirmaPagamentoEmLote`,
          dadosEnvio,
        );

        addToast({
          type: 'success',
          title: 'Salvo com sucesso',
        });
        handleFechar(true);

      } catch (error: any) {
        const mensagemErro = getResponseErrors(error);
        const statusErro = error?.response?.status;
        addToast({
          type: 'error',
          title: 'Erro ao salvar pagamento',
          description: mensagemErro,
        });

      } finally {
        setLoading(false);
      }
    },
    [addToast, parcelasPagLote, pagamentosRealizados, valoresAdicionados, descontoAplicado, dataHoraServidor, handleFechar, total],
  );

  const handleChange = useCallback(
    e => {
      const CprCodigo = e.target.name;
      const isChecked = e.target.checked;

      const newItens = parcelasPagLote.map(it => {
        if (it.CprCodigo?.toString() === CprCodigo)
          return { ...it, selecionado: isChecked };
        return it;
      });

      const its = newItens
        .filter(item => {
          return item.selecionado;
        })
        .map(i => i.CprCodigo)
        .join('-');

      setParcelasPagLote(newItens);
    },
    [parcelasPagLote],
  );


  return (
    <Container className="p-1" style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading backgroundBlack={false} />}
      {parcelasPagLote && parcelasPagLote.length > 0 && (
        <Row noGutters>
          <Col>
            <Card>
              <CardHeader>
                <span>
                  <FaUser size={15} /> <b>PARCELAS</b>
                </span>

                {/* {clinica.ConfigBoleto?.Emite && (
                  <ButtonAdd
                    type="button"
                    onClick={() => handleGeraBoletos()}
                    title="gerar Boletos"
                  >
                    <FaBarcode size={15} /> Gerar Boletos
                  </ButtonAdd>
                )} */}
              </CardHeader>

              <CardBody>
                {/* <Table
                  className="text-secondary"
                  style={{ marginBottom: '9px' }}
                  hover
                  size="sm"
                > */}
                <Row noGutters className="bg-light text-dark pt-1 pb-2 pl-1">
                  <Col md={1} className="px-1">
                    ID
                  </Col>
                  <Col className="px-1">Parcelas</Col>
                  {/* <Col md={1} className="px-1">
                    Pago
                  </Col>
                  <Col md={1} className="px-1">
                    Ativa
                  </Col>
                  <Col md={1} className="px-1">
                    Excluída
                  </Col> */}
                  <Col md={2} className="px-1">
                    Data
                  </Col>
                  <Col md={1} className="px-1">
                    Multa
                  </Col>
                  <Col md={1} className="px-1">
                    Juros
                  </Col>
                  <Col md={2} className="px-1">
                    Total
                  </Col>
                  {/* <Col md={2} className="px-1">
                    -
                  </Col> */}
                </Row>
                <Form
                  ref={formRefParcela}
                  // initialData={InitialData}
                  onSubmit={handleSubmit}
                >
                  {parcelasPagLote?.map((parcela, index) => (
                    <Row
                      noGutters
                      className="bg-light text-dark p-1"
                      key={parcela.CprCodigo.toString()}
                    >
                      <Checkbox
                        name={parcela.CprCodigo?.toString() || ''}
                        onChange={handleChange}
                        disabled={
                          ((index !== 0 ||
                            parcelasPagLote[index + 1]?.selecionado) &&
                            (!parcelasPagLote[index - 1]?.selecionado ||
                              parcelasPagLote[index + 1]?.selecionado)) ||
                          (pagamentosRealizados.length > 0 || descontoAplicado.valor !== '0')
                        }
                      />
                      <Col md={1} className="px-1">
                        {parcela.CprCodigo}
                      </Col>
                      <Col className="px-1">{parcela.CprHistorico}</Col>
                      {/* <Col md={1} className="px-1">
                        {parcela.CprPagoFormatado}
                      </Col>
                      <Col md={1} className="px-1">
                        {parcela.CprParcelaAtiva ? 'Sim' : 'Não'}
                      </Col>
                      <Col md={1} className="px-1">
                        {parcela.CprExcluida ? 'Sim' : 'Não'}
                      </Col> */}
                      <Col md={2} className="px-1">
                        {parcela.CprDataVencFormatado}
                      </Col>
                      <Col md={1} className="px-1">
                        {parcela.CprMulta}
                      </Col>
                      <Col md={1} className="px-1">
                        {parcela.CprJuros}
                      </Col>
                      <Col md={2} className="px-1">
                        {parcela.valorTotalFormatado}
                      </Col>
                      {/* <Col
                        md={2}
                        className="d-flex justify-content-between align-items-center px-1"
                      > */}
                      {/* <Button
                          data-id={parcela.CprCodigo}
                          type="submit"
                          size="sm"
                          style={{ padding: '1px 8px' }}
                          className="bg-success"
                          disabled={parcela.CprPago}
                        >
                          <FaCheckCircle size={14} />
                        </Button> */}

                      {/* <Button
                          type="button"
                          size="sm"
                          variant={
                            !parcela.CprExcluida
                              ? 'outline-danger'
                              : 'outline-primary'
                          }
                          style={{ padding: '1px 8px' }}
                          onClick={handleAtivarDesativar}
                          title={!parcela.CprExcluida ? 'Excluir' : 'Restaurar'}
                          disabled={parcela.CprPago}
                        >
                          {!parcela.CprExcluida && <FaTrash size={14} />}
                          {parcela.CprExcluida && <FaTrashRestore size={14} />}
                        </Button> */}
                      {/* </Col> */}
                    </Row>
                  ))}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      <Row className="mt-2">
        <Col className="d-flex justify-content-end align-items-center">
          {`Total de parcelas selecionadas: ${total.count} - Valor Total: ${total.valorTotalFormatado}`}
        </Col>
      </Row>
      <Row className={`no-gutters mt-2 px-2 ${total.count === 0 ? 'd-none' : ''}`}>
        <Col md="12" lg="12" className="form-group pr-2">
          {total.count > 0 && (
            <>
              <FormaPagamento
                CprValor={total?.valorTotal || 0}
                cprCodigo={parcelasPagLote[0]?.CprCodigo || 0}
                ehSangria={false}
                estaPago={false}
                ehTroco={false}
                ehDespesaOuOutrasReceitas={false}
                handleAddedPagamento={setPagamentosRealizados}
                handleAddedDesconto={setDescontoAplicado}
                pagamentosRealizados={pagamentosRealizados}
                descontoAplicado={descontoAplicado}
                handleFechar={() => setShowFuncaoAnonima(false)}
                pegaValor={() => total?.valorTotal || 100000}
                EhConvenio={false}
                apenasUmaForma
              />
              <Row>
                <Col md="12" xl="12">
                  <Button
                    type="button"
                    variant="success"
                    style={{ paddingTop: '0.1rem', paddingBottom: '0.1rem' }}
                    size="sm"
                    className="btn btn-sm btn-success mx-1"
                    onClick={() => formRefParcela.current?.submitForm()}
                  >
                    Salvar
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default PagamentoEmLote;
